import type { CreateWorkforcePhoneChangeCommand } from "@billiard/contracts";
import { isCreateWorkforcePhoneChangeCommand } from "@billiard/contracts";

const storageKey = "billiard.workforce-phone-change-recovery.v1";

const labeledSecretPattern =
  /(?:密码|口令|验证码|密钥|令牌|password|passcode|otp|token|secret|api[\s_-]*key|key)(?:(?:\s*(?:是|为|值|[:：=])\s*)|(?:[ \t]+))\S{3,}/iu;
const sixDigitCodePattern = /(^|\D)\d{6}(?!\d)/u;
const opaqueSecretPattern = /(^|[\s:：=])(?:[A-Za-z0-9+/_=-]{24,})(?=$|\s)/u;

export interface WorkforcePhoneChangeRecoveryIdentity {
  tenantId: string;
  storeId: string;
  accountId: string;
}

export interface WorkforcePhoneChangeRecovery {
  schemaVersion: 1;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
  identity: WorkforcePhoneChangeRecoveryIdentity;
  command: CreateWorkforcePhoneChangeCommand;
  recordedAt: string;
}

export type WorkforcePhoneChangeRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: WorkforcePhoneChangeRecovery }
  | { status: "OTHER_IDENTITY" }
  | { status: "CONFLICT" }
  | { status: "STORAGE_UNAVAILABLE" }
  | { status: "SENSITIVE_REASON" };

interface ApiFailureLike {
  code?: unknown;
  statusCode?: unknown;
  workforcePhoneChangeGuardState?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function identityMatches(
  left: WorkforcePhoneChangeRecoveryIdentity,
  right: WorkforcePhoneChangeRecoveryIdentity,
): boolean {
  return (
    left.tenantId === right.tenantId &&
    left.storeId === right.storeId &&
    left.accountId === right.accountId
  );
}

function isIdentity(value: unknown): value is WorkforcePhoneChangeRecoveryIdentity {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["accountId", "storeId", "tenantId"]) &&
    typeof value.tenantId === "string" &&
    value.tenantId.length > 0 &&
    typeof value.storeId === "string" &&
    value.storeId.length > 0 &&
    typeof value.accountId === "string" &&
    value.accountId.length > 0
  );
}

export function workforcePhoneChangeReasonContainsSecret(reason: string): boolean {
  return (
    labeledSecretPattern.test(reason) ||
    sixDigitCodePattern.test(reason) ||
    opaqueSecretPattern.test(reason)
  );
}

export function workforcePhoneChangeCommandsMatch(
  left: CreateWorkforcePhoneChangeCommand,
  right: CreateWorkforcePhoneChangeCommand,
): boolean {
  return left.reason === right.reason && left.idempotencyKey === right.idempotencyKey;
}

function isRecovery(value: unknown): value is WorkforcePhoneChangeRecovery {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["command", "identity", "recordedAt", "schemaVersion", "state"]) &&
    value.schemaVersion === 1 &&
    (value.state === "CLAIMED" || value.state === "OUTCOME_UNKNOWN") &&
    isIdentity(value.identity) &&
    isCreateWorkforcePhoneChangeCommand(value.command) &&
    !workforcePhoneChangeReasonContainsSecret(value.command.reason) &&
    typeof value.recordedAt === "string" &&
    Number.isFinite(Date.parse(value.recordedAt))
  );
}

function persist(recovery: WorkforcePhoneChangeRecovery): boolean {
  if (workforcePhoneChangeReasonContainsSecret(recovery.command.reason)) return false;
  const serialized = JSON.stringify(recovery);
  try {
    localStorage.setItem(storageKey, serialized);
    return localStorage.getItem(storageKey) === serialized;
  } catch {
    return false;
  }
}

export function readWorkforcePhoneChangeRecovery(
  identity: WorkforcePhoneChangeRecoveryIdentity,
): WorkforcePhoneChangeRecoveryView {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    return { status: "STORAGE_UNAVAILABLE" };
  }
  if (raw === null) return { status: "NONE" };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: "STORAGE_UNAVAILABLE" };
  }
  if (!isRecovery(parsed)) return { status: "STORAGE_UNAVAILABLE" };
  if (!identityMatches(parsed.identity, identity)) return { status: "OTHER_IDENTITY" };
  if (parsed.state === "CLAIMED") {
    const unknown = { ...parsed, state: "OUTCOME_UNKNOWN" as const };
    return persist(unknown)
      ? { status: "OWNED", recovery: unknown }
      : { status: "STORAGE_UNAVAILABLE" };
  }
  return { status: "OWNED", recovery: parsed };
}

export function claimWorkforcePhoneChangeRecovery(
  identity: WorkforcePhoneChangeRecoveryIdentity,
  command: CreateWorkforcePhoneChangeCommand,
): WorkforcePhoneChangeRecoveryView {
  if (workforcePhoneChangeReasonContainsSecret(command.reason)) {
    return { status: "SENSITIVE_REASON" };
  }
  if (!isCreateWorkforcePhoneChangeCommand(command)) return { status: "CONFLICT" };
  const current = readWorkforcePhoneChangeRecovery(identity);
  if (current.status === "OWNED") {
    return workforcePhoneChangeCommandsMatch(current.recovery.command, command)
      ? current
      : { status: "CONFLICT" };
  }
  if (current.status !== "NONE") return current;
  const recovery: WorkforcePhoneChangeRecovery = {
    schemaVersion: 1,
    state: "CLAIMED",
    identity,
    command,
    recordedAt: new Date().toISOString(),
  };
  if (!persist(recovery)) return { status: "STORAGE_UNAVAILABLE" };
  try {
    const verified = JSON.parse(localStorage.getItem(storageKey) ?? "null") as unknown;
    return isRecovery(verified) &&
      identityMatches(verified.identity, identity) &&
      workforcePhoneChangeCommandsMatch(verified.command, command)
      ? { status: "OWNED", recovery: verified }
      : { status: "STORAGE_UNAVAILABLE" };
  } catch {
    return { status: "STORAGE_UNAVAILABLE" };
  }
}

export function markWorkforcePhoneChangeOutcomeUnknown(
  recovery: WorkforcePhoneChangeRecovery,
  identity: WorkforcePhoneChangeRecoveryIdentity,
): WorkforcePhoneChangeRecoveryView {
  const current = readWorkforcePhoneChangeRecovery(identity);
  if (
    current.status !== "OWNED" ||
    !workforcePhoneChangeCommandsMatch(current.recovery.command, recovery.command)
  ) {
    return current.status === "NONE" ? { status: "CONFLICT" } : current;
  }
  const unknown = { ...current.recovery, state: "OUTCOME_UNKNOWN" as const };
  return persist(unknown)
    ? { status: "OWNED", recovery: unknown }
    : { status: "STORAGE_UNAVAILABLE" };
}

export function clearWorkforcePhoneChangeRecovery(
  recovery: WorkforcePhoneChangeRecovery,
  identity: WorkforcePhoneChangeRecoveryIdentity,
): WorkforcePhoneChangeRecoveryView {
  const current = readWorkforcePhoneChangeRecovery(identity);
  if (
    current.status !== "OWNED" ||
    !workforcePhoneChangeCommandsMatch(current.recovery.command, recovery.command)
  ) {
    return current.status === "NONE" ? { status: "CONFLICT" } : current;
  }
  try {
    localStorage.removeItem(storageKey);
    return localStorage.getItem(storageKey) === null
      ? { status: "NONE" }
      : { status: "STORAGE_UNAVAILABLE" };
  } catch {
    return { status: "STORAGE_UNAVAILABLE" };
  }
}

export function workforcePhoneChangeOutcomeIsUnknown(
  error: unknown,
  recoveryState?: WorkforcePhoneChangeRecovery["state"],
): boolean {
  if (!isRecord(error)) return true;
  const failure = error as ApiFailureLike;
  if (failure.workforcePhoneChangeGuardState === "CLEARED") return false;
  if (recoveryState === "OUTCOME_UNKNOWN") return true;
  if (typeof failure.statusCode === "number") {
    if (failure.statusCode >= 400 && failure.statusCode < 500) return false;
    if (failure.statusCode >= 500) return true;
  }
  if (typeof failure.code !== "string") return true;
  return ![
    "REQUEST_VALIDATION_FAILED",
    "NATIVE_UPDATE_REQUIRED",
    "NATIVE_SECURE_SESSION_UNAVAILABLE",
  ].includes(failure.code);
}

export function resetWorkforcePhoneChangeRecoveryForTests(): void {
  if (!import.meta.env.DEV) throw new Error("test-only phone change recovery reset");
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // A storage-unavailable test intentionally makes this best-effort cleanup fail.
  }
}
